import { useEffect, useMemo, useState } from "react";
import { navigateTo } from "../../router";
import { CarnivalTable } from "./CarnivalTable";
import { eventSummary } from "./event-summary";
import { CARNIVAL_OF_SINS_REPLAY_FIXTURES } from "./replay-fixtures.generated";
import {
  buildCarnivalOfSinsReplaySnapshots,
  type CarnivalOfSinsReplaySnapshot,
} from "./replay-runner";
import { ScorePad } from "./ScorePad";
import "./styles/carnival-of-sins-tokens.css";
import "./styles/carnival-of-sins-client.css";

interface ReplayScreenProps {
  readonly scenario: string;
}

const SPEED_OPTIONS = [
  { label: "느리게", ms: 1400 },
  { label: "보통", ms: 800 },
  { label: "빠르게", ms: 400 },
] as const;

function clampStepIndex(
  index: number,
  snapshots: readonly CarnivalOfSinsReplaySnapshot[],
): number {
  if (snapshots.length === 0) return 0;
  return Math.min(Math.max(0, index), snapshots.length - 1);
}

function parseInteger(value: string | null): number | null {
  if (value === null || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

function resolveStepIndexFromSearch(
  snapshots: readonly CarnivalOfSinsReplaySnapshot[],
): number {
  if (typeof window === "undefined" || snapshots.length === 0) return 0;
  const params = new URLSearchParams(window.location.search);
  const seq = parseInteger(params.get("seq"));
  if (seq !== null) {
    const index = snapshots.findIndex((s) => s.event.sequenceNumber === seq);
    if (index >= 0) return index;
  }
  const step = parseInteger(params.get("step"));
  if (step !== null) return clampStepIndex(step, snapshots);
  return 0;
}

function replaceStepQuery(
  index: number,
  snapshots: readonly CarnivalOfSinsReplaySnapshot[],
): void {
  if (typeof window === "undefined" || snapshots.length === 0) return;
  const url = new URL(window.location.href);
  url.searchParams.set("step", String(index));
  url.searchParams.delete("seq");
  window.history.replaceState(null, "", `${url.pathname}${url.search}`);
}

export function ReplayScreen({ scenario }: ReplayScreenProps) {
  const fixture = CARNIVAL_OF_SINS_REPLAY_FIXTURES[scenario] ?? null;
  const snapshots = useMemo(
    () => (fixture ? buildCarnivalOfSinsReplaySnapshots(fixture.events) : []),
    [fixture],
  );
  const namesByPlayerId = useMemo(
    () =>
      fixture
        ? Object.fromEntries(fixture.players.map((p) => [p.id, p.name]))
        : {},
    [fixture],
  );

  const [stepIndex, setStepIndex] = useState(() =>
    resolveStepIndexFromSearch(snapshots),
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [speedMs, setSpeedMs] = useState<number>(SPEED_OPTIONS[1].ms);
  const [showRecap, setShowRecap] = useState(false);

  const safeStepIndex = clampStepIndex(stepIndex, snapshots);
  const current = snapshots[safeStepIndex] ?? snapshots[0] ?? null;
  const maxIndex = snapshots.length - 1;

  useEffect(() => {
    setStepIndex(resolveStepIndexFromSearch(snapshots));
  }, [snapshots]);

  useEffect(() => {
    if (!isPlaying) return;
    if (safeStepIndex >= maxIndex) {
      setIsPlaying(false);
      return;
    }
    const timer = setTimeout(() => {
      const next = clampStepIndex(safeStepIndex + 1, snapshots);
      setStepIndex(next);
      replaceStepQuery(next, snapshots);
    }, speedMs);
    return () => clearTimeout(timer);
  }, [isPlaying, safeStepIndex, maxIndex, speedMs, snapshots]);

  function setReplayStep(index: number): void {
    const next = clampStepIndex(index, snapshots);
    setStepIndex(next);
    replaceStepQuery(next, snapshots);
  }

  if (!fixture || !current) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-primary p-6 text-text-primary">
        <section className="max-w-sm rounded-md border border-border-primary bg-surface-secondary p-6 text-center">
          <h1 className="mb-2 font-semibold text-lg">
            리플레이를 찾을 수 없습니다
          </h1>
          <p className="mb-4 text-sm text-text-secondary">{scenario}</p>
          <button
            type="button"
            className="rounded-md bg-accent-blue px-4 py-2 font-semibold text-sm text-white"
            onClick={() => navigateTo("/")}
          >
            닫기
          </button>
        </section>
      </div>
    );
  }

  const state = current.state;
  const isGameOver = state.phase === "GAME_OVER";
  const activePlayerId =
    state.phase === "REVEAL_RESOLVE"
      ? state.revealOrder[state.nextToRevealIndex]
      : null;
  const devilName =
    namesByPlayerId[state.devilCardHolderId] ?? state.devilCardHolderId;
  const phaseLabel =
    state.phase === "SECRET_SELECT"
      ? "비밀 선택"
      : state.phase === "REVEAL_RESOLVE"
        ? "공개 · 해결"
        : "종료";
  const winnerNames = (state.winnerIds ?? [])
    .map((id) => namesByPlayerId[id] ?? id)
    .join(" & ");

  return (
    <div
      data-testid="carnival-of-sins-replay-screen"
      className="min-h-screen px-3 py-4 sm:px-6"
      style={{ background: "var(--cn-carnival-of-sins-bg)" }}
    >
      <div className="mx-auto flex max-w-xl flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="m-0 font-bold text-lg text-text-primary">
              Carnival of Sins 리플레이
            </h1>
            <p className="mt-0.5 text-text-muted text-xs">
              {fixture.players.map((p) => p.name).join(" · ")} · 시뮬레이션
            </p>
          </div>
          <button
            type="button"
            className="rounded-md border border-border-primary bg-surface-secondary px-3 py-1.5 text-text-secondary text-xs"
            onClick={() => navigateTo("/")}
          >
            ‹ 뒤로
          </button>
        </div>

        {isGameOver ? (
          <div
            data-testid="carnival-of-sins-replay-result"
            className="rounded-md border border-accent-blue-light bg-accent-blue-bg px-4 py-3 text-center font-semibold text-accent-blue text-sm"
          >
            👑 {winnerNames || "무승부"} — Monarch of the Carnival
          </div>
        ) : (
          <div className="rounded-md border border-border-subtle bg-surface-secondary px-4 py-2 text-center text-sm text-text-secondary">
            라운드 <span className="font-semibold">{state.round}/7</span> ·{" "}
            {phaseLabel} · 데빌 카드{" "}
            <span className="font-semibold">{devilName}</span>
          </div>
        )}

        <div className="flex justify-center">
          {/* Dice are always public (see engine's filterStateForViewer) — `viewer.dice`
              and `state.dice` are always identical for this game; using the viewer
              projection here is just consistent discipline, not an active mask. */}
          <CarnivalTable
            players={state.players}
            seatingOrder={state.seatingOrder}
            devilCardHolderId={state.devilCardHolderId}
            activePlayerId={activePlayerId}
            dice={current.viewer.dice}
          />
        </div>

        <ScorePad
          players={state.players}
          scoreHistoryByPlayerId={state.scoreHistoryByPlayerId}
          winnerIds={state.winnerIds}
        />

        <div
          data-testid="carnival-of-sins-replay-toolbar"
          className="flex flex-col gap-2 rounded-md border border-border-subtle bg-surface-secondary p-3"
        >
          <p
            data-testid="carnival-of-sins-replay-event-summary"
            className="text-center text-sm text-text-primary"
          >
            {eventSummary(namesByPlayerId, snapshots, safeStepIndex, showRecap)}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="carnival-of-sins-replay-prev"
              disabled={safeStepIndex === 0}
              onClick={() => {
                setIsPlaying(false);
                setReplayStep(safeStepIndex - 1);
              }}
              className="rounded-md border border-border-primary bg-surface-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
            >
              ◀ 이전
            </button>
            <button
              type="button"
              data-testid="carnival-of-sins-replay-play-toggle"
              disabled={safeStepIndex >= maxIndex}
              onClick={() => setIsPlaying((p) => !p)}
              className="rounded-md bg-accent-blue px-3 py-2 font-semibold text-sm text-white disabled:opacity-40"
            >
              {isPlaying ? "⏸ 일시정지" : "▶ 자동재생"}
            </button>
            <button
              type="button"
              data-testid="carnival-of-sins-replay-next"
              disabled={safeStepIndex === maxIndex}
              onClick={() => {
                setIsPlaying(false);
                setReplayStep(safeStepIndex + 1);
              }}
              className="rounded-md border border-border-primary bg-surface-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
            >
              다음 ▶
            </button>
            <span
              data-testid="carnival-of-sins-replay-step-count"
              className="ml-auto text-text-secondary text-xs"
            >
              {safeStepIndex + 1}/{snapshots.length}
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={maxIndex}
            value={safeStepIndex}
            data-testid="carnival-of-sins-replay-step-slider"
            onChange={(event) => {
              setIsPlaying(false);
              setReplayStep(Number(event.target.value));
            }}
            className="w-full"
          />
          <div className="flex items-center justify-between text-text-muted text-xs">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                data-testid="carnival-of-sins-replay-recap-toggle"
                data-active={showRecap}
                checked={showRecap}
                onChange={(event) => setShowRecap(event.target.checked)}
              />
              전체 공개 (복기)
            </label>
            <label className="flex items-center gap-2">
              재생 속도
              <select
                data-testid="carnival-of-sins-replay-speed"
                value={speedMs}
                onChange={(event) => setSpeedMs(Number(event.target.value))}
                className="rounded-md border border-border-primary bg-surface-primary px-2 py-1 text-text-primary"
              >
                {SPEED_OPTIONS.map((option) => (
                  <option key={option.ms} value={option.ms}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
